import { FileText, Search } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import Input from '../Input.jsx';
import ChatInputDocumentSearchCard from './ChatInputDocumentSearchCard.jsx';
import DocumentDeleteConfirmModal from './DocumentDeleteConfirmModal.jsx';

const ChatInputDocumentSearch = ({
  searchValue = '',
  onSearchChange,
  documents = [],
  searchPlaceholder = 'Search documents...',
  emptyTitle = 'No documents available',
  emptySubtitle = 'Files generated through the agent will appear in this workspace.',
  width = '445px',
  height = 'auto',
  maxHeight = '490px',
  className = '',
  onMenuAction,
  onDeleteClick,
  onCardClick,
  onDeleteModalOpenChange,
}) => {
  const [documentPendingDelete, setDocumentPendingDelete] = useState(null);
  const isEmpty = documents.length === 0;
  const query = (searchValue || '').trim().toLowerCase();
  const filteredDocuments = useMemo(() => {
    if (!query) return documents;
    return documents.filter((doc) => {
      const title = (doc?.title || '').toLowerCase();
      const description = (doc?.description || '').toLowerCase();
      const agentName = (doc?.agentName || '').toLowerCase();
      const driveName = (doc?.driveName || '').toLowerCase();
      return (
        title.includes(query) ||
        description.includes(query) ||
        agentName.includes(query) ||
        driveName.includes(query)
      );
    });
  }, [documents, query]);
  const isSearchEmpty = !isEmpty && filteredDocuments.length === 0;
  const resolvedWidth = typeof width === 'number' ? `${width}px` : width;
  const resolvedHeight = typeof height === 'number' ? `${height}px` : height;
  const resolvedMaxHeight = typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight;

  const handleOpenDeleteConfirm = useCallback((doc) => {
    if (!doc?.id) return;
    setDocumentPendingDelete(doc);
    onDeleteModalOpenChange?.(true);
  }, [onDeleteModalOpenChange]);

  const handleCloseDeleteConfirm = useCallback(() => {
    setDocumentPendingDelete(null);
    onDeleteModalOpenChange?.(false);
  }, [onDeleteModalOpenChange]);

  const handleConfirmDelete = useCallback(() => {
    if (documentPendingDelete) {
      onDeleteClick?.(documentPendingDelete);
    }
    setDocumentPendingDelete(null);
    onDeleteModalOpenChange?.(false);
  }, [documentPendingDelete, onDeleteClick, onDeleteModalOpenChange]);

  return (
    <div
      className={[
        'box-border flex flex-col gap-6 rounded-lg border border-[0.5px] border-edge bg-surface px-4 py-6',
        isEmpty ? 'min-h-[187px] items-center justify-center text-center' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      style={{
        width: resolvedWidth,
        height: resolvedHeight,
        maxHeight: resolvedMaxHeight,
      }}
    >
      {isEmpty ? (
        <div className="flex max-w-[435px] flex-col items-center gap-2 text-center">
          <FileText size={20} className="text-content-secondary" />
          <p className="text-base font-semibold text-content">{emptyTitle}</p>
          <p className="text-sm text-content-secondary">{emptySubtitle}</p>
        </div>
      ) : (
        <>
          <Input
            value={searchValue}
            onChange={(event) => onSearchChange?.(event.target.value)}
            placeholder={searchPlaceholder}
            leftIcon={Search}
            className="h-10 !rounded-md !text-sm"
          />
          <p className="text-sm text-content-secondary">
            {`${filteredDocuments.length} ${filteredDocuments.length === 1 ? 'Document' : 'Documents'}`}
          </p>
          {isSearchEmpty ? (
            <div className="flex min-h-0 flex-1 items-center justify-center text-sm text-content-secondary">
              No matching documents
            </div>
          ) : (
            <div className="scrollbar-aimsos-auto-sm flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto">
              {filteredDocuments.map((doc) => (
                <ChatInputDocumentSearchCard
                  key={doc.id}
                  doc={doc}
                  onMenuAction={onMenuAction}
                  onDeleteClick={handleOpenDeleteConfirm}
                  onCardClick={onCardClick}
                />
              ))}
            </div>
          )}
        </>
      )}

      <DocumentDeleteConfirmModal
        isOpen={Boolean(documentPendingDelete)}
        documentTitle={documentPendingDelete?.title || ''}
        onCancel={handleCloseDeleteConfirm}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};

export default ChatInputDocumentSearch;
